import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Loader2, ChevronLeft, ChevronRight, CheckCircle2, Clock, Sparkles, ArrowLeft } from 'lucide-react';
import { useTriageSession } from './hooks/useTriageSession';
import { useCopilot } from './hooks/useCopilot';
import { useFitAssess } from './hooks/useFitAssess';
import { useAuth } from '../../contexts/AuthContext';
import { fillPlaceholders } from '../../utils/fillPlaceholders';
import StepNavigator from './components/StepNavigator';
import ScriptPanel from './components/ScriptPanel';
import ClosePanel from './components/ClosePanel';
import CopilotPanel from './components/CopilotPanel';
import ScriptPicker from './components/ScriptPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sentinel id for the synthetic, UI-only CLOSE stage appended after the last
// real script step. Never collides with a script.data.steps id.
const CLOSE_STEP_ID = '__close__';

function fmtTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// "just now" / "30s ago" / "2m ago" relative to an ISO timestamp.
function fmtAgo(iso, now) {
  if (!iso) return '';
  const secs = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (secs < 5) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

/**
 * Real save status driven by the actual session lifecycle in useTriageSession:
 *  - isSaving (PATCH in flight)        -> "Saving…"
 *  - isDirty (edited, not yet flushed) -> "Unsaved changes" (transient)
 *  - clean + lastSaved                 -> "Saved · <relative time>"
 *  - clean + never saved yet           -> "Saved"
 * No permanent "Unsaved" — clean state always reads as saved.
 */
function SaveStatus({ isSaving, isDirty, lastSaved }) {
  // Tick once a second so the relative time stays fresh while clean & idle.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (isSaving) {
    return (
      <span className="text-white/60 flex items-center gap-1">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
      </span>
    );
  }
  if (isDirty) {
    return (
      <span className="text-yellow-400 flex items-center gap-1">
        <Clock className="w-3.5 h-3.5" /> Unsaved changes
      </span>
    );
  }
  return (
    <span className="text-[#00d4aa] flex items-center gap-1">
      <CheckCircle2 className="w-3.5 h-3.5" />
      {lastSaved ? `Saved · ${fmtAgo(lastSaved, now)}` : 'Saved'}
    </span>
  );
}

function TriageCockpit() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const { state, dispatch, flush } = useTriageSession();
  const { user } = useAuth();
  const loadedRef = useRef(false);
  // Active question = which spoken-line note field is focused within the step.
  const [activeLineIdx, setActiveLineIdx] = useState(0);

  // Load call + script once. (No-op on the picker route where callId is undefined.)
  useEffect(() => {
    if (!callId || loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        const callRes = await axios.get(`${API}/triage/calls/${callId}`);
        const call = callRes.data.call;
        if (call.status === 'completed') {
          navigate(`/triage/${callId}/summary`, { replace: true });
          return;
        }
        const scriptRes = await axios.get(`${API}/triage/scripts/${call.script_id}`);
        const script = scriptRes.data.script;
        const session = call.session || {};
        dispatch({
          type: 'INIT_CALL',
          callId,
          scriptId: call.script_id,
          trade: call.trade,
          prospectName: call.prospect_name || null,
          script,
          currentStepId: session.current_step_id || (script.data?.steps?.[0]?.id ?? null),
          questionNotes: session.question_notes || {},
          objectionsClicked: session.objections_clicked || [],
          fitSignals: session.fit_signals || {},
          fitAi: session.fit_ai || {},
          elapsedSeconds: session.elapsed_seconds || 0,
        });
      } catch (e) {
        toast.error('Failed to load call');
        navigate('/triage');
      }
    })();
  }, [callId, dispatch, navigate]);

  // Elapsed timer.
  useEffect(() => {
    if (state.status !== 'active') return;
    const id = setInterval(() => dispatch({ type: 'TICK_ELAPSED' }), 1000);
    return () => clearInterval(id);
  }, [state.status, dispatch]);

  // Warn before leaving an active call.
  useEffect(() => {
    const handler = (e) => {
      if (state.status === 'active') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state.status]);

  const steps = state.script?.data?.steps || [];
  // Are we on the synthetic CLOSE stage (after the last real step)?
  const onClose = state.currentStepId === CLOSE_STEP_ID;
  const currentStep = onClose ? null : steps.find((s) => s.id === state.currentStepId) || null;
  const currentIdx = onClose ? steps.length : steps.findIndex((s) => s.id === state.currentStepId);
  const objectionBank = state.script?.data?.objection_bank || [];
  const fitMatrix = state.script?.data?.fit_matrix || [];
  const workshopPitch = state.script?.data?.workshop_pitch || null;
  const notFitClose = state.script?.data?.not_fit_close || '';
  const isIcp = state.script?.is_icp;

  // Display-time placeholder substitution. Never mutates stored script data.
  const fillOpts = { prospectName: state.prospectName, callerName: user?.name };
  const fill = (t) => fillPlaceholders(t, fillOpts);

  // Substituted copy of the current step so the caller sees real names in the
  // spoken lines without touching what's persisted.
  const displayStep = currentStep
    ? { ...currentStep, spoken_lines: (currentStep.spoken_lines || []).map(fill) }
    : null;

  // Anchor the copilot to the active question (the focused note field).
  const currentLines = currentStep?.spoken_lines || [];
  // Pass the substituted line into the copilot so the model uses the real name.
  const currentQuestion = fill(currentLines[activeLineIdx] || '');
  const currentNote = state.questionNotes?.[state.currentStepId]?.[activeLineIdx] || '';

  // Next scripted question: next line in this step, else first line of next step.
  let nextQuestion = '';
  if (activeLineIdx + 1 < currentLines.length) {
    nextQuestion = currentLines[activeLineIdx + 1];
  } else if (currentIdx >= 0 && currentIdx + 1 < steps.length) {
    nextQuestion = (steps[currentIdx + 1].spoken_lines || [])[0] || '';
  }
  nextQuestion = fill(nextQuestion);

  const { triggerCopilot } = useCopilot({
    callId,
    currentStepId: state.currentStepId,
    currentLineIdx: activeLineIdx,
    currentQuestion,
    currentNote,
    nextQuestion,
    clickedObjectionIds: state.objectionsClicked,
    // Flush in-memory notes to the DB before the copilot reads them.
    flushSession: flush,
    dispatch,
  });

  const { assessFit } = useFitAssess({ callId, flushSession: flush, dispatch });

  // Does the session hold any captured answer? Gate the first auto-assess on it
  // so we don't burn an LLM call on an empty call.
  const hasAnyNote = Object.values(state.questionNotes || {}).some((step) =>
    Object.values(step || {}).some((n) => n && n.trim())
  );

  // Auto-run fit assessment once on mount (if there are notes — e.g. a resumed
  // call) and again whenever the caller advances to a different step. Debounced
  // and in-flight-guarded inside the hook so it never double-fires.
  const fitRanOnceRef = useRef(false);
  useEffect(() => {
    if (state.status !== 'active') return;
    if (!fitRanOnceRef.current) {
      fitRanOnceRef.current = true;
      if (hasAnyNote) assessFit();
      return;
    }
    // Step changed after the initial run -> re-assess with the latest notes.
    assessFit();
  }, [state.currentStepId, state.status, assessFit, hasAnyNote]);

  const onNoteChange = (stepId, lineIdx, note) => {
    dispatch({ type: 'UPDATE_QUESTION_NOTE', stepId, lineIdx, note });
    triggerCopilot();
  };

  const onToggleObjection = (objectionId) => {
    dispatch({ type: 'TOGGLE_OBJECTION', objectionId });
    triggerCopilot({ immediate: true });
  };

  const onSelectStep = async (stepId) => {
    dispatch({ type: 'SET_STEP', stepId });
    setActiveLineIdx(0);
    await flush();
  };

  const goPrev = () => {
    if (onClose) {
      // Back from CLOSE -> the last real script step.
      if (steps.length) onSelectStep(steps[steps.length - 1].id);
    } else if (currentIdx > 0) {
      onSelectStep(steps[currentIdx - 1].id);
    }
  };
  const goNext = () => {
    if (onClose) return;
    if (currentIdx < steps.length - 1) {
      onSelectStep(steps[currentIdx + 1].id);
    } else if (currentIdx === steps.length - 1) {
      // After the last real step the caller advances to the synthetic CLOSE stage.
      onSelectStep(CLOSE_STEP_ID);
    }
  };

  const completeCall = async () => {
    await flush();
    try {
      await axios.post(`${API}/triage/calls/${callId}/complete`, { outcome: 'follow_up' });
      dispatch({ type: 'COMPLETE_CALL' });
      navigate(`/triage/${callId}/summary`);
    } catch (e) {
      toast.error('Failed to complete call');
    }
  };

  // No callId => the "new call" picker route.
  if (!callId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a] p-6">
        <h1 className="text-2xl font-semibold text-white mb-1">New Triage Call</h1>
        <p className="text-white/50 text-sm mb-8">Pick the trade script that matches the prospect.</p>
        <ScriptPicker />
      </div>
    );
  }

  if (state.status === 'idle') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0a0a0a] text-white/40">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  // Progress spans the real steps PLUS the synthetic CLOSE stage (steps.length + 1).
  const totalStages = steps.length + 1;
  const progress = totalStages ? ((currentIdx + 1) / totalStages) * 100 : 0;
  // Complete Call lives on the CLOSE stage (the true final stage).
  const isLast = onClose;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a] flex flex-col">
      {/* Header */}
      <div className="glass-morphism border-b border-white/10 px-6 py-3 flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/triage')}
          className="text-white/60 hover:text-white hover:bg-white/5 -ml-2 flex-shrink-0"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to calls
        </Button>
        <div className="min-w-0">
          <h1 className="text-base font-semibold text-white truncate">{state.script?.display_name || state.trade}</h1>
          <p className="text-xs text-white/40 truncate">Triage Call</p>
        </div>
        <div className="flex-1">
          <Progress value={progress} className="h-1.5 bg-white/10" />
        </div>
        <div className="flex items-center gap-1.5 text-xs text-white/50 font-mono">
          <Clock className="w-3.5 h-3.5" /> {fmtTime(state.elapsedSeconds)}
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <SaveStatus isSaving={state.isSaving} isDirty={state.isDirty} lastSaved={state.lastSaved} />
        </div>
      </div>

      {/* 3-panel body */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[260px_1fr_320px] gap-4 p-4 overflow-hidden">
        <div className="glass-morphism rounded-xl p-4 overflow-hidden">
          <StepNavigator
            steps={steps}
            currentStepId={state.currentStepId}
            questionNotes={state.questionNotes}
            onSelectStep={onSelectStep}
            fitMatrix={fitMatrix}
            fitSignals={state.fitSignals}
            fitAi={state.fitAi}
            fitAiLoading={state.fitAiLoading}
            fitAiError={state.fitAiError}
            onSetFitSignal={(signal, value) => dispatch({ type: 'SET_FIT_SIGNAL', signal, value })}
            onClearFitSignal={(signal) => dispatch({ type: 'CLEAR_FIT_SIGNAL', signal })}
            onRecheckFit={() => assessFit({ immediate: true })}
            closeStepId={CLOSE_STEP_ID}
          />
        </div>

        <div className="glass-morphism rounded-xl p-5 overflow-y-auto">
          {onClose ? (
            <ClosePanel
              workshopPitch={workshopPitch}
              notFitClose={notFitClose}
              fitMatrix={fitMatrix}
              fitSignals={state.fitSignals}
              fitAi={state.fitAi}
              isIcp={isIcp}
              fill={fill}
            />
          ) : (
            <ScriptPanel
              step={displayStep}
              notes={state.questionNotes[state.currentStepId]}
              activeLineIdx={activeLineIdx}
              onNoteChange={onNoteChange}
              onFocusQuestion={setActiveLineIdx}
              objectionBank={objectionBank}
              objectionsClicked={state.objectionsClicked}
              onToggleObjection={onToggleObjection}
            />
          )}
        </div>

        <div className="glass-morphism rounded-xl p-4 overflow-y-auto">
          {onClose ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <Sparkles className="w-8 h-8 text-[#00d4aa]/60 mb-3" />
              <p className="text-sm text-white/60 leading-relaxed">
                Final stage. The fit verdict on the left guides the call — read the
                highlighted block, then complete the call.
              </p>
            </div>
          ) : (
            <CopilotPanel
              suggestion={state.copilotSuggestion}
              loading={state.copilotLoading}
              error={state.copilotError}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="glass-morphism border-t border-white/10 px-6 py-3 flex items-center justify-between">
        <Button variant="outline" onClick={goPrev} disabled={currentIdx <= 0}
          className="border-white/10 text-white/70">
          <ChevronLeft className="w-4 h-4 mr-1" /> Prev
        </Button>
        {isLast ? (
          <Button onClick={completeCall} className="bg-[#00d4aa] hover:bg-[#00d4aa]/90 text-black font-medium">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Complete Call
          </Button>
        ) : (
          <Button onClick={goNext} className="bg-[#00d4aa] hover:bg-[#00d4aa]/90 text-black font-medium">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default TriageCockpit;
